import * as BalanceAccountsActions from "./account.actions";

import {createEntityAdapter, EntityAdapter} from "@ngrx/entity";
import {createReducer, on} from "@ngrx/store";
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {BalanceAccount, BalanceAccountSearchParams} from '@accounting/_common/models/balance-account.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const accountFeatureKey = 'account';

export function sortEntities(a: BalanceAccount, b: BalanceAccount) {
    return a.code!.localeCompare(b.code!);
}

export const adapter: EntityAdapter<BalanceAccount> = createEntityAdapter<BalanceAccount>({
    sortComparer: sortEntities
});

export type BalanceAccountState = EntityStateModel<BalanceAccount, BalanceAccountSearchParams>;

export const initialState: BalanceAccountState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: [],
});

export const accountReducer = createReducer(
    initialState,

    on(BalanceAccountsActions.CheckState, (state, action) => {
        if (action.payload.modalView) {
            return adapter.removeAll({
                ...state,
                savedFilter: !action.payload.close && state.filter ? state.filter : undefined,
                pagination: initialState.pagination,
                filter: action.payload.close ? state.savedFilter : undefined,
            });
        } else {
            if (action.payload.close) {
                return adapter.setAll(action.payload.items, {
                    ...state,
                    pagination: {...state.pagination, number: 0},
                    componentOpened: false
                });
            }

            return {
                ...state,
                componentOpened: true
            };
        }
    }),

    on(BalanceAccountsActions.LoadList, (state, action) => ({
        ...state,
        listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.ids)
    })),

    on(BalanceAccountsActions.LoadListSuccess, (state, action) => {
        return action.payload.clearState
            ? adapter.setAll(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined

            })
            : adapter.upsertMany(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            });
    }),

    on(BalanceAccountsActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(BalanceAccountsActions.SetFilter, (state, action) => ({
        ...state,
        filter: action.payload
    })),

    on(BalanceAccountsActions.SetPagination, (state, action) => ({
        ...state,
        pagination: {...state.pagination, number: action.payload ? StoredComponentHelperService.setNextPage(state.pagination, state.ids?.length) : 0}
    })),

    on(BalanceAccountsActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(BalanceAccountsActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),

    on(BalanceAccountsActions.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state.filter, action.payload);
        if (savedItemAvailableByFilter) {
            return adapter.setOne(action.payload!, {
                ...state,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
                pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
            });
        }

        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(BalanceAccountsActions.DeleteItem, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, true)
    })),

    on(BalanceAccountsActions.DeleteItemSuccess, (state, action) => {
        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(BalanceAccountsActions.DeleteItemFailure, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false)
    })),

    on(BalanceAccountsActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();

const isAvailableByFilter = (searchParams: BalanceAccountSearchParams | undefined, item: BalanceAccount): boolean => {
    if (!searchParams?.includeDeleted && item.archived) {
        return false;
    }

    if (!searchParams) {
        return true;
    }

    if (searchParams.balanceAccountType && searchParams.balanceAccountType != item.type) {
        return false;
    }

    if ((searchParams.offBalance || searchParams.offBalance === false) && item.offBalance !== searchParams.offBalance) {
        return false;
    }

    if (searchParams.subAccounts && searchParams.subAccounts != item.subAccounts) {
        return false;
    }

    if (
        searchParams.query &&
        item.name &&
        item.name.toLowerCase().indexOf(searchParams.query.toLowerCase()) == -1 &&
        item.code &&
        item.code.toLowerCase().indexOf(searchParams.query.toLowerCase()) == -1
    ) {
        return false;
    }

    return true;
};
